import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { opacity2hex, sortArray } from "utils/miscUtils";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import useSearchHook from "hooks/useSearch";
import { defaultPageNo } from "utils/constants";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getCardTypeTableColumns } from "utils/tableHelper/extraTableHelper";
import {
  copyNotification,
  deleteNotification,
  getNotifications,
  updateNotificationStatus,
} from "services/notificationService";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import useConsent from "hooks/useConsent";
import ViewImageModal from "screens/modals/ViewImageModal";
import useOpenClose from "hooks/useOpenClose";

const useStyles = makeStyles(((theme: any) => ({
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  pickerContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "end",
    justifyContent: "space-between",
  },
  buttonContainer: {
    minWidth: 90,
    maxWidth: 120,
    display: "flex",
  },
  button: {
    height: 30,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    textTransform: "none",
    width: "100% !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
      maxWidth: "100% !important",
    },
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
})) as any);

const RowActions = ({ rowId, data, actionHandlers }: any) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRowAction = (data: any) => {
    setAnchorEl(null);
    navigate(configData.routes.notifications.edit?.replace(":id", data?._id));
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.NOTIFICATIONS.DELETE_ROLES
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleRowAction(data)}>Edit</MenuItem>
        <MenuItem onClick={() => actionHandlers?.handleDisable(data?._id)}>
          {data?.isActive ? "Disable" : "Enable"}
        </MenuItem>
        <MenuItem onClick={() => actionHandlers?.handleCopyNotification(data?._id)}>Copy</MenuItem>
        {hasDeletePermission && (
          <MenuItem onClick={() => actionHandlers?.handleDelete(data)}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const Notifications = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [listData, setListData] = useState([]);
  const [orderDirection, setOrderDirection] = useState("asc");
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.LEAGUE.UPDATE_ROLES
  );

  const { isOpen, close, open, extraData, updateExtra } = useOpenClose();

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: listData?.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...listData], orderDirection, column);
    setListData(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const createLeague = () => navigate(configData.routes.notifications.create);

  const fetchData = async (_search?: any) => {
    setLoading(true);
    try {
      let payload = { pageNo, pageSize, sortDir: orderDirection };
      if (searchValue) payload["searchValue"] = searchValue;
      const resp = await getNotifications(payload);
      setListData(
        resp?.data?.items?.map((item) => ({
          ...item,
          isActiveText: item.isActive ? "YES" : "NO",
          rowDisabled: !item?.isActive,
          copiedFrom: item?.copiedFrom || "-",
        }))
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message);
    }
  };

  const onSearch = (value: any) => {
    // reset filters if any
    _onSearch(value);
    onChangePage(defaultPageNo);
  };

  const onDisable = async (id) => {
    try {
      const resp = await updateNotificationStatus(id);
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const onDelete = async (id) => {
    try {
      const resp = await deleteNotification(id);
      showSnackbar(resp?.message);
      fetchData(searchValue);
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

    const onCopy = async (id: any) => {
    try {
      setLoading(true);
      const resp = await copyNotification(id);
      await fetchData();
      showSnackbar(resp?.message);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({ itemName: data?.title, id: data?._id });
  };

  const openModel = (data: any) => {
    updateExtra({
      imageUrl: data.imageUrl,
    });
    open();
  };

  useEffect(() => {
    const initialColumns = getCardTypeTableColumns().filter(
      (col) => col.id !== "actions" || hasEditPermission
    );
    setColumns(initialColumns);

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchValue]);

  return (
    <ContentShell
      title="Notifications"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a notification"
            onSearch={onSearch}
          />
          {sessionHelper.hasRole(PermittedRoles.LEAGUE.CREATE_ROLES) && (
            <CustomButton onClick={createLeague}>Create</CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.container}>
        <Table
          columns={columns}
          data={listData}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={listData.length}
          highlightAltRow={true}
          actions={RowActions}
          onChangePage={onChangePage}
          onColumnClick={onColumnClick}
          onRowsPerPageChange={onPageSizeChange}
          actionHandlers={{
            openModal: openModel,
            handleDisable: onDisable,
            handleDelete: _openConsentDialog,
            handleCopyNotification: onCopy,
          }}
        />
        <ViewImageModal
          label="Notification Image"
          imageSource={extraData.imageUrl}
          isOpen={isOpen}
          close={close}
          customStyle={{ width: "60%" }}
        />
        <DeleteConsentDialog
          itemName={constentDetails.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={() => onDelete(constentDetails.id)}
        />
      </Box>
    </ContentShell>
  );
};

export default Notifications;
